/**
 * Fullscreen image gallery with reorder + delete for a product.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Trash, X, CaretLeft, CaretRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function GalleryLightbox({ gallery, setGallery, onSwapped }) {
  const original = gallery.originals?.[gallery.idx] || null;
  const enhanced = gallery.images[gallery.idx];
  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const prev = () =>
    setGallery({
      ...gallery,
      idx: (gallery.idx - 1 + gallery.images.length) % gallery.images.length,
    });
  const next = () =>
    setGallery({ ...gallery, idx: (gallery.idx + 1) % gallery.images.length });

  const regenerate = async () => {
    setRegenerating(true);
    try {
      // Use the original as source if we have it, otherwise the current.
      const source = original || enhanced;
      const previousImage = enhanced; // ← guardamos para "Revertir"
      const { data } = await api.post("/products/enhance-image", {
        image_base64: source,
        nombre_producto: gallery.nombre || "",
        categoria: "",
      });
      if (!data?.image_base64) throw new Error("Sin imagen");
      const newImages = [...gallery.images];
      const newOriginals = [...(gallery.originals || [])];
      newImages[gallery.idx] = data.image_base64;
      while (newOriginals.length < newImages.length) newOriginals.push("");
      if (!newOriginals[gallery.idx]) newOriginals[gallery.idx] = source;
      await api.put(`/products/${gallery.product_id}`, {
        imagenes_base64: newImages,
        imagenes_originales_base64: newOriginals,
      });
      setGallery({ ...gallery, images: newImages, originals: newOriginals });
      onSwapped?.();

      // Toast con acción "Revertir" disponible 8 segundos
      const idxAtRegen = gallery.idx;
      toast.success("Nueva versión generada", {
        duration: 8000,
        action: {
          label: "Revertir",
          onClick: async () => {
            try {
              const revertImages = [...newImages];
              revertImages[idxAtRegen] = previousImage;
              await api.put(`/products/${gallery.product_id}`, {
                imagenes_base64: revertImages,
                imagenes_originales_base64: newOriginals,
              });
              setGallery((g) => g ? { ...g, images: revertImages } : g);
              onSwapped?.();
              toast.info("Foto revertida a la versión anterior");
            } catch {
              toast.error("No se pudo revertir");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo regenerar");
    } finally {
      setRegenerating(false);
    }
  };

  const deletePhoto = async () => {
    if (!window.confirm(`Eliminar esta foto del producto?`)) return;
    // Optimistic UI: update the gallery immediately so the user sees the
    // change without waiting for the network round-trip. Snapshot in case we
    // need to roll back on failure.
    const idxAtDelete = gallery.idx;
    const prevImages = gallery.images;
    const prevOriginals = gallery.originals || [];
    const newImages = prevImages.filter((_, i) => i !== idxAtDelete);
    const newOriginals = prevOriginals.filter((_, i) => i !== idxAtDelete);
    setDeleting(true);
    if (newImages.length === 0) {
      setGallery(null);
    } else {
      const newIdx = Math.min(idxAtDelete, newImages.length - 1);
      setGallery({ ...gallery, images: newImages, originals: newOriginals, idx: newIdx });
    }
    onSwapped?.();
    try {
      await api.delete(`/products/${gallery.product_id}/images/${idxAtDelete}`);
      toast.success("Foto eliminada");
    } catch (e) {
      // Roll back local UI state.
      setGallery({ ...gallery, images: prevImages, originals: prevOriginals, idx: idxAtDelete });
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
      onSwapped?.();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={() => setGallery(null)}
      data-testid="gallery-lightbox"
    >
      <button
        className="absolute top-4 right-4 text-white p-2 hover:bg-white/10"
        onClick={() => setGallery(null)}
        data-testid="gallery-close"
      >
        <X size={20} weight="bold" />
      </button>
      <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-white/70">{gallery.nombre}</div>
            <div className="text-xs text-white/50 font-mono mt-1">
              {gallery.idx + 1} / {gallery.images.length}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={regenerate}
              disabled={regenerating || deleting}
              className="text-xs px-3 py-1.5 inline-flex items-center gap-1.5 bg-klein text-white hover:bg-klein-dark disabled:opacity-50"
              title="Generar otra versión catálogo"
              data-testid="regenerate-image"
              data-help="La IA genera una versión nueva de esta foto. La anterior se reemplaza."
            >
              {regenerating ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Regenerando…
                </>
              ) : (
                <>
                  <Sparkle size={14} weight="fill" /> Regenerar
                </>
              )}
            </button>
            <button
              onClick={deletePhoto}
              disabled={regenerating || deleting}
              className="text-xs px-3 py-1.5 inline-flex items-center gap-1.5 bg-[#FF2A00] text-white hover:bg-[#cc2200] disabled:opacity-50"
              title="Eliminar esta foto"
              data-testid="delete-image"
              data-help="Elimina permanentemente esta foto del producto."
            >
              {deleting ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Eliminando…
                </>
              ) : (
                <>
                  <Trash size={14} weight="bold" /> Eliminar
                </>
              )}
            </button>
          </div>
        </div>

        <div className="relative mt-4 bg-zinc-900 border border-zinc-800">
          <img
            src={`data:image/jpeg;base64,${enhanced}`}
            alt={`${gallery.nombre} ${gallery.idx + 1}`}
            className="w-full max-h-[70vh] object-contain"
          />
          {gallery.images.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white text-ink p-2 hover:bg-klein hover:text-white"
                data-testid="gallery-prev"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              <button
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white text-ink p-2 hover:bg-klein hover:text-white"
                data-testid="gallery-next"
              >
                <CaretRight size={20} weight="bold" />
              </button>
            </>
          )}
        </div>

        {gallery.images.length > 1 && (
          <div className="mt-3 flex gap-1.5 overflow-x-auto">
            {gallery.images.map((b64, i) => (
              <button
                key={`${b64?.slice(-16) || "img"}-${i}`}
                onClick={() => setGallery({ ...gallery, idx: i })}
                className={`relative shrink-0 w-16 h-16 border ${
                  gallery.idx === i ? "border-klein ring-2 ring-klein" : "border-zinc-700"
                }`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img
                  src={`data:image/jpeg;base64,${b64}`}
                  alt={`thumb ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// AI-powered audience research modal — proposes WHERE (subreddits, FB groups,
// hashtags, search queries) the seller should publish this product manually.
